import React, { useState } from "react";
import AuditTimeline from "@/modules/racket_lab_admin/AuditTimeline";
import { api } from "@/lib/api";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * AuditPromotionViewer — Sub-sprint D-β-α.
 *
 * Read-only audit history viewer for a user_id. Calls
 * `GET /admin/racket-lab/_ops/audit-promotion?user_id=…` and renders
 * the resulting `audit[]` via the existing `AuditTimeline` component.
 */
export const AuditPromotionViewer = ({ dataTestId = "rl-ops-audit" }) => {
  const t = useRacketLabAdminT();
  const [userId, setUserId] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async () => {
    setErr(null);
    setData(null);
    if (!userId.trim()) {
      setErr({ detail: t("ops.audit_viewer.user_id_required") });
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/admin/racket-lab/_ops/audit-promotion?user_id=${encodeURIComponent(userId.trim())}`);
      setData(r.data);
    } catch (e) {
      setErr({ status: e?.response?.status, detail: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={dataTestId} className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-3">
      <h3 className="text-sm font-medium text-slate-800">{t("ops.audit.promotion_lookup")}</h3>
      <div className="flex flex-col gap-2 md:flex-row md:items-end">
        <label className="flex flex-1 flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("ops.audit_viewer.user_id_label")}</span>
          <input
            data-testid={`${dataTestId}-input`}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={loading}
            placeholder={t("ops.audit_viewer.user_id_placeholder")}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
          />
        </label>
        <button
          type="button"
          data-testid={`${dataTestId}-submit`}
          onClick={onSubmit}
          disabled={!userId.trim() || loading}
          className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${userId.trim() && !loading ? "bg-sky-600 hover:bg-sky-700" : "bg-sky-300 cursor-not-allowed"}`}
        >
          {t("ops.audit_viewer.submit")}
        </button>
      </div>

      {err && (
        <div data-testid={`${dataTestId}-error`} className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {t("ops.common.error_prefix")} {err.status || ""}: {err.detail || t("ops.common.error_unknown")}
        </div>
      )}

      {data && (
        <div data-testid={`${dataTestId}-result`} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="rounded-full bg-slate-100 px-2 py-0.5">found: <code>{String(data.found)}</code></span>
            {data.membership_status && (<span className="rounded-full bg-slate-100 px-2 py-0.5">status: <code>{data.membership_status}</code></span>)}
            {data.current_role && (<span className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-800">role: <code>{data.current_role}</code></span>)}
            <span className="rounded-full bg-slate-100 px-2 py-0.5">audit_count: <code>{data.audit_count ?? 0}</code></span>
          </div>
          <AuditTimeline entries={data.audit || []} dataTestId={`${dataTestId}-timeline`} />
        </div>
      )}
    </div>
  );
};

export default AuditPromotionViewer;
